/**
 * RequestAbortManager
 *
 * Utility module to manage a single global AbortController for API request cancellation
 * based on page or route navigation.
 *
 * Features:
 * ✅ Cancels pending API requests automatically when navigating to a new page
 * ✅ Tracks the current page (via `currentPageName`)
 * ✅ Provides the current AbortSignal to attach to Axios requests
 *
 * How it works:
 * - Call `setPage(newPageName)` whenever the user navigates to a new page (e.g., in router.beforeEach)
 * - Any pending requests from the previous page will be aborted automatically
 * - `getSignal()` provides the latest AbortSignal to pass into Axios config
 *
 */

let abortController = null; // Stores the current AbortController
let currentPageName = null; // Tracks the current page name

const ignoredUrls = ["view_store", "show_profile"];

/**
 * Sets the current page name.
 * If navigating to a new page, cancels any pending requests from the previous page.
 * Creates a new AbortController for the new page.
 *
 * @param {string} name - The name of the new page/route
 */
export function setPage(name) {
  if (currentPageName !== name) {
    if (abortController) {
      abortController.abort(); // Cancel all requests from the previous page
    }
    abortController = new AbortController(); // Create a new controller for the new page
    currentPageName = name;
  }
}

/**
 * Returns the current AbortSignal for attaching to requests.
 * @returns {AbortSignal|null}
 */
export function getSignal(url) {
  if (ignoredUrls.some((ignoredUrl) => url.includes(ignoredUrl))) {
    return null; // Ignore signals for specific URLs
  }
  return abortController?.signal;
}
